// ── Stokvel billing (client side) ──
// Reads a stokvel's subscription state, and kicks off a Paystack subscription by
// asking our backend to initialise a transaction, then redirecting to Paystack's
// hosted card page. The secret key + signing all happen server-side; the browser
// only ever sees the public checkout URL.

import { authHeader } from './firebase';

const BASE = process.env.REACT_APP_ENGINE_URL ?? 'http://localhost:3001';

// Single plan at R250/month — receipts & audit trail are now INCLUDED for everyone
// (both ids resolve to the same price so older references keep working).
export const STOKVEL_PRICES = { core: 250, core_receipts: 250 };

// Every new stokvel gets a free trial of this many days from creation, during which
// recording works exactly like a paid subscription (receipts included) so the group
// can try the full product before paying.
export const TRIAL_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

// Convert a Firestore Timestamp | Date | millis | ISO string to millis, or 0.
function toMillis(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();        // Firestore Timestamp
  if (typeof v.seconds === 'number') return v.seconds * 1000;        // plain {seconds}
  const t = new Date(v).getTime();
  return isNaN(t) ? 0 : t;
}

function fmtDate(ms) {
  return ms ? new Date(ms).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
}

// The end of a stokvel's free trial, in millis. Uses the explicit `trialEndsAt`
// stamped at creation; for groups created before trials existed, it falls back to
// `createdAt + TRIAL_DAYS` so every group has a well-defined window without a
// migration.
export function trialEndsMs(stokvel) {
  const explicit = toMillis(stokvel && stokvel.trialEndsAt);
  if (explicit) return explicit;
  const created = toMillis(stokvel && stokvel.createdAt);
  return created ? created + TRIAL_DAYS * DAY_MS : 0;
}

// Live subscription state for a stokvel. `active` (which gates recording) is true
// while EITHER paid-and-not-lapsed OR still inside the free trial. Priority:
// paid → trial → expired. During the trial the group gets the full feature set,
// including receipts, so `hasReceipts` is true.
export function subscriptionState(stokvel) {
  const now = Date.now();
  const untilMs = toMillis(stokvel && stokvel.subscriptionUntil);
  const paid = (stokvel && stokvel.subscriptionStatus === 'active') && untilMs > now;

  const trialEnd = trialEndsMs(stokvel);
  const inTrial = !paid && trialEnd > now;
  const trialDaysLeft = inTrial ? Math.max(1, Math.ceil((trialEnd - now) / DAY_MS)) : 0;

  const active = paid || inTrial;
  const status = paid ? 'paid' : inTrial ? 'trial' : 'expired';
  // Auto-renew is on unless the chair cancelled (server sets subscriptionAutoRenew=false).
  const autoRenew = !(stokvel && stokvel.subscriptionAutoRenew === false);
  const cancelled = paid && !autoRenew;

  return {
    active,
    status,                          // 'paid' | 'trial' | 'expired'
    paid,
    inTrial,
    autoRenew,                       // false once cancelled — no further billing
    cancelled,                       // paid & cancelled: access until `until`, won't renew
    plan: (stokvel && stokvel.subscriptionPlan) || null,
    // Paid groups get whatever they bought; trial groups get everything to try.
    hasReceipts: active, // receipts & audit trail are now CORE — included for every active/trial group
    until: untilMs || null,
    untilLabel: fmtDate(untilMs),
    trialEnd: trialEnd || null,
    trialEndLabel: fmtDate(trialEnd),
    trialDaysLeft,
  };
}

// We remember the reference of the payment we just started so we can confirm it when
// the browser returns from Paystack (no webhook — the server verifies on return).
const PENDING_REF_KEY = 'tlk_ps_ref';
const PENDING_SID_KEY = 'tlk_ps_sid';

// Start (or change) a subscription: ask the backend to initialise a Paystack
// transaction on the Hailio account, stash the reference, then redirect the browser
// to Paystack's hosted, secure card page.
export async function startSubscription(stokvelId, plan) {
  const res = await fetch(BASE + '/api/paystack/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    // Send our origin so Paystack redirects back to THIS host (the app is served on
    // two hosts; localStorage + the Firebase session are per-origin, so the return
    // must land on the same host the chair subscribed from).
    body: JSON.stringify({ stokvelId, plan, origin: window.location.origin }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err && err.error) || ('Could not start payment (' + res.status + ')'));
  }
  const { authorizationUrl, reference } = await res.json();
  if (!authorizationUrl) throw new Error('Could not start payment. Please try again.');
  try {
    localStorage.setItem(PENDING_REF_KEY, reference);
    localStorage.setItem(PENDING_SID_KEY, stokvelId);
  } catch (_) { /* private mode — verify can still run from the stokvelId in context */ }
  window.location.href = authorizationUrl; // top-level navigation to Paystack
}

// The stokvelId + reference we stored before redirecting (if any), for the return handler.
export function pendingPaystack() {
  try {
    return { stokvelId: localStorage.getItem(PENDING_SID_KEY), reference: localStorage.getItem(PENDING_REF_KEY) };
  } catch (_) { return { stokvelId: null, reference: null }; }
}

export function clearPendingPaystack() {
  try { localStorage.removeItem(PENDING_REF_KEY); localStorage.removeItem(PENDING_SID_KEY); } catch (_) {}
}

// Confirm the payment after returning from Paystack. Activates the subscription
// server-side and returns { ok, until }. Falls back to the passed stokvelId if the
// stored one is missing.
export async function verifyPaystack(stokvelId, reference) {
  const res = await fetch(BASE + '/api/paystack/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ stokvelId, reference }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && data.error) || ('Could not confirm the payment (' + res.status + ')'));
  return data; // { ok, until }
}

// Cancel the recurring subscription (chair only). We stop future monthly re-charges;
// the group stays active until the already-paid-through date, then won't renew.
export async function cancelSubscription(stokvelId) {
  const res = await fetch(BASE + '/api/paystack/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ stokvelId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && data.error) || ('Could not cancel the subscription (' + res.status + ')'));
  return data; // { ok: true, activeUntil }
}
